import React, { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Coach, Group } from "../api/client";
import {
  BulkTimeChange,
  Place,
  ScheduleFields,
  ScheduledTraining,
  Training,
  TrainingFields,
  TrainingSchedule,
  WEEKDAYS,
  dayString,
  daysInMonth,
  formatDay,
  formatTimeRange,
  isoWeekday,
  nextDay,
  parseDay,
  today,
  trainingsApi,
  weekdayOf,
} from "../api/trainings";
import { useConfirm } from "../components/ConfirmDialog";
import PageLoader from "../components/PageLoader";
import TrainingAttendance from "./TrainingAttendance";
import "./TrainingsPage.css";

const MONTHS = [
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
];

const OTHER_PLACE = "other";

// Guards the day-by-day count against a mistyped year.
const MAX_PERIOD_DAYS = 400;

type Month = { year: number; month: number };

type TrainingForm = {
  date: string;
  type: string;
  startTime: string;
  endTime: string;
  placeID: string;
  title: string;
  coachID: string;
  applyOnward: boolean;
};

type ScheduleForm = {
  weekdays: number[];
  startTime: string;
  endTime: string;
  placeID: string;
  title: string;
  coachID: string;
  from: string;
  to: string;
};

const byName = <T extends { name: string }>(a: T, b: T) => a.name.localeCompare(b.name, "ru");

const byDateAndTime = <T extends { date: string; start_time?: string | null }>(a: T, b: T) =>
  a.date.localeCompare(b.date) || (a.start_time ?? "").localeCompare(b.start_time ?? "");

// The app titles a weekly schedule by the place's address.
const scheduleTitleOf = (place: Place) => place.address || place.name;

const weekdayList = (weekdays: number[]) =>
  weekdays
    .slice()
    .sort((a, b) => a - b)
    .map((d) => WEEKDAYS[d - 1]?.short.toLowerCase())
    .filter(Boolean)
    .join(", ");

function plural(n: number, one: string, few: string, many: string): string {
  const tens = n % 100;
  const ones = n % 10;
  if (ones === 1 && tens !== 11) return one;
  if (ones >= 2 && ones <= 4 && (tens < 12 || tens > 14)) return few;
  return many;
}

function countDays(from: string, to: string, weekdays: number[]): number | null {
  if (!from || !to || from > to) return 0;
  let count = 0;
  let day = from;
  for (let i = 0; i < MAX_PERIOD_DAYS; i++) {
    if (weekdays.includes(isoWeekday(day))) count++;
    if (day === to) return count;
    day = nextDay(day);
  }
  return null;
}

function defaultDate(month: Month): string {
  const now = today();
  const { year, month: m } = parseDay(now);
  return year === month.year && m === month.month ? now : dayString(month.year, month.month, 1);
}

function scheduleFormOf(schedule: TrainingSchedule | undefined, places: Place[]): ScheduleForm {
  const from = today();
  const { year, month } = parseDay(from);
  const nextYear = month === 12 ? year + 1 : year;
  const nextMonth = month === 12 ? 1 : month + 1;
  const base = { coachID: "", from, to: dayString(nextYear, nextMonth, daysInMonth(nextYear, nextMonth)) };
  if (!schedule) {
    return { ...base, weekdays: [], startTime: "18:00", endTime: "19:30", placeID: "", title: "" };
  }
  const place = places.find((p) => scheduleTitleOf(p) === schedule.title);
  return {
    ...base,
    weekdays: schedule.weekdays.slice().sort((a, b) => a - b),
    startTime: schedule.start_time,
    endTime: schedule.end_time,
    placeID: place ? place.id : OTHER_PLACE,
    title: place ? "" : schedule.title,
  };
}

export default function TrainingsPage() {
  const [groups, setGroups] = useState<Group[]>([]);
  const [places, setPlaces] = useState<Place[]>([]);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [groupID, setGroupID] = useState("");
  const [month, setMonth] = useState<Month>(() => {
    const { year, month: m } = parseDay(today());
    return { year, month: m };
  });
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [groupSchedules, setGroupSchedules] = useState<TrainingSchedule[]>([]);
  const [groupScheduled, setGroupScheduled] = useState<ScheduledTraining[]>([]);
  const [editing, setEditing] = useState<Training | "new" | null>(null);
  const [form, setForm] = useState<TrainingForm | null>(null);
  const [attendanceFor, setAttendanceFor] = useState<Training | null>(null);
  const [scheduleForm, setScheduleForm] = useState<ScheduleForm>(() => scheduleFormOf(undefined, []));
  const latestLoad = useRef(0);
  const prefilledFor = useRef<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const { confirm, dialog } = useConfirm();

  useEffect(() => {
    const optional = (what: string) => (err: unknown) => {
      console.error(`Failed to load ${what}`, err);
      return null;
    };
    (async () => {
      try {
        const [groupList, placeList, coachList] = await Promise.all([
          trainingsApi.listGroups(),
          trainingsApi.listPlaces().catch(optional("places")),
          trainingsApi.listCoaches().catch(optional("coaches")),
        ]);
        const sortedGroups = (groupList ?? []).slice().sort(byName);
        setGroups(sortedGroups);
        setPlaces((placeList ?? []).slice().sort(byName));
        setCoaches((coachList ?? []).slice().sort(byName));
        setGroupID(sortedGroups[0]?.id ?? "");
      } catch (err: any) {
        console.error("Failed to load groups", err);
        setError(err?.message || "Не удалось загрузить группы");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const loadGroup = useCallback(async () => {
    if (!groupID) return;
    // Switching group or month quickly must not let an older answer win.
    const n = ++latestLoad.current;
    const start = dayString(month.year, month.month, 1);
    const end = dayString(month.year, month.month, daysInMonth(month.year, month.month));
    try {
      const [list, scheduleList, scheduledList] = await Promise.all([
        trainingsApi.listTrainings(groupID, start, end),
        trainingsApi.listSchedules(),
        trainingsApi.listScheduledTrainings(),
      ]);
      if (n !== latestLoad.current) return;
      const sorted = (list ?? []).slice().sort(byDateAndTime);
      setTrainings(sorted);
      setAttendanceFor((current) => (current ? sorted.find((t) => t.id === current.id) ?? current : current));
      const own = (scheduleList ?? []).filter((s) => s.group_id === groupID);
      setGroupSchedules(own);
      setGroupScheduled((scheduledList ?? []).filter((s) => s.group_id === groupID).sort(byDateAndTime));
      if (prefilledFor.current !== groupID) {
        prefilledFor.current = groupID;
        setScheduleForm(scheduleFormOf(own[own.length - 1], places));
      }
    } catch (err: any) {
      if (n !== latestLoad.current) return;
      console.error("Failed to load trainings", err);
      setError(err?.message || "Не удалось загрузить тренировки");
    }
  }, [groupID, month, places]);

  useEffect(() => {
    loadGroup();
  }, [loadGroup]);

  const run = async (action: () => Promise<unknown>, failure: string, onDone?: () => void) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      onDone?.();
    } catch (err: any) {
      console.error("Training action failed", err);
      setError(err?.message || failure);
    } finally {
      setBusy(false);
      await loadGroup();
    }
  };

  const chooseGroup = (id: string) => {
    setError(null);
    setGroupID(id);
    setEditing(null);
    setAttendanceFor(null);
  };

  const shiftMonth = (delta: number) => {
    setError(null);
    setMonth((m) => {
      const index = m.year * 12 + (m.month - 1) + delta;
      return { year: Math.floor(index / 12), month: (index % 12) + 1 };
    });
  };

  const edited = editing !== null && editing !== "new" ? editing : null;

  const startNew = () => {
    setError(null);
    setEditing("new");
    setForm({
      date: defaultDate(month),
      type: "training",
      startTime: "",
      endTime: "",
      placeID: places[0]?.id ?? OTHER_PLACE,
      title: "",
      coachID: "",
      applyOnward: false,
    });
    formRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };

  const startEditing = (t: Training) => {
    setError(null);
    setEditing(t);
    setForm({
      date: t.date,
      type: t.type || "training",
      startTime: t.start_time ?? "",
      endTime: t.end_time ?? "",
      placeID: t.place_id ?? OTHER_PLACE,
      title: t.place_id ? "" : t.title,
      coachID: t.coach_id != null ? String(t.coach_id) : "",
      applyOnward: false,
    });
    formRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };

  const setField = (changes: Partial<TrainingForm>) => setForm((f) => (f ? { ...f, ...changes } : f));

  let formTitle = "";
  if (form) {
    if (form.placeID === OTHER_PLACE) formTitle = form.title.trim();
    else if (edited && form.placeID === (edited.place_id ?? "")) formTitle = edited.title;
    else formTitle = places.find((p) => p.id === form.placeID)?.name ?? "";
  }
  const startChanged = !!form && !!edited && form.startTime !== (edited.start_time ?? "");
  const endChanged = !!form && !!edited && form.endTime !== (edited.end_time ?? "");
  const canApplyOnward =
    !!form &&
    !!edited?.group_id &&
    form.type === "training" &&
    ((startChanged && form.startTime !== "") || (endChanged && form.endTime !== ""));
  const trainingTimesInvalid = !!form && form.startTime !== "" && form.endTime !== "" && form.endTime <= form.startTime;
  const canSubmitTraining = !busy && !!form && form.date !== "" && formTitle !== "" && !trainingTimesInvalid;

  const submitTraining = async (e: FormEvent) => {
    e.preventDefault();
    if (!form || !canSubmitTraining) return;
    const fields: TrainingFields = {
      date: form.date,
      title: formTitle,
      type: form.type,
      coach_id: form.coachID ? Number(form.coachID) : null,
      place_id: form.placeID === OTHER_PLACE ? null : form.placeID,
      start_time: form.startTime || null,
      end_time: form.endTime || null,
    };
    const target = edited;
    const onward: BulkTimeChange | null =
      target?.group_id && form.applyOnward && canApplyOnward
        ? {
            group_id: target.group_id,
            weekday: isoWeekday(form.date),
            after_date: form.date,
            ...(startChanged && form.startTime ? { start_time: form.startTime } : {}),
            ...(endChanged && form.endTime ? { end_time: form.endTime } : {}),
          }
        : null;
    await run(
      async () => {
        if (!target) {
          await trainingsApi.createTraining(groupID, fields);
          return;
        }
        await trainingsApi.updateTraining(target.id, fields);
        if (onward) await trainingsApi.changeTimeOnward(onward);
      },
      target ? "Не удалось сохранить тренировку" : "Не удалось создать тренировку",
      () => setEditing(null)
    );
  };

  const removeTraining = async (t: Training) => {
    const confirmed = await confirm({
      title: `Удалить тренировку ${formatDay(t.date)}?`,
      body: (
        <>
          <p>Вместе с тренировкой удалятся отметки посещаемости и баллы детей за неё.</p>
          <p>Детям группы придёт уведомление об отмене.</p>
          <p>Отменить удаление нельзя.</p>
        </>
      ),
      confirmLabel: "Удалить тренировку",
      danger: true,
    });
    if (!confirmed) return;
    await run(
      async () => {
        await trainingsApi.deleteTraining(t.id);
        // Attendance goes with the training, but the children's totals are
        // only recounted on request.
        await trainingsApi.recalculatePoints();
      },
      "Не удалось удалить тренировку",
      () => {
        setAttendanceFor((current) => (current?.id === t.id ? null : current));
        setEditing((current) => (current !== "new" && current?.id === t.id ? null : current));
      }
    );
  };

  const currentSchedule = groupSchedules[groupSchedules.length - 1];
  const setSchedule = (changes: Partial<ScheduleForm>) => setScheduleForm((f) => ({ ...f, ...changes }));
  const toggleWeekday = (iso: number) =>
    setScheduleForm((f) => ({
      ...f,
      weekdays: f.weekdays.includes(iso) ? f.weekdays.filter((d) => d !== iso) : [...f.weekdays, iso],
    }));

  const scheduleTitle =
    scheduleForm.placeID === OTHER_PLACE
      ? scheduleForm.title.trim()
      : (() => {
          const place = places.find((p) => p.id === scheduleForm.placeID);
          return place ? scheduleTitleOf(place) : "";
        })();
  const scheduleWeekdays = scheduleForm.weekdays.slice().sort((a, b) => a - b);
  const scheduleTimesValid =
    scheduleForm.startTime !== "" && scheduleForm.endTime !== "" && scheduleForm.startTime < scheduleForm.endTime;
  const canSaveSchedule = !busy && !!groupID && scheduleWeekdays.length > 0 && scheduleTitle !== "" && scheduleTimesValid;
  const dayCount = countDays(scheduleForm.from, scheduleForm.to, scheduleWeekdays);
  const canGenerate = canSaveSchedule && dayCount !== null && dayCount > 0;

  let periodHint = "";
  if (scheduleForm.from && scheduleForm.to && scheduleForm.from > scheduleForm.to) periodHint = "Конец периода раньше начала";
  else if (dayCount === null) periodHint = "Период длиннее года — выберите короче";
  else if (scheduleWeekdays.length > 0 && dayCount === 0) periodHint = "В этом периоде нет выбранных дней недели";
  else if (dayCount > 0) periodHint = `Получится ${dayCount} ${plural(dayCount, "тренировка", "тренировки", "тренировок")}`;

  const saveSchedule = async (generate: boolean) => {
    if (!(generate ? canGenerate : canSaveSchedule)) return;
    const fields: ScheduleFields = {
      group_id: groupID,
      title: scheduleTitle,
      weekdays: scheduleWeekdays,
      start_time: scheduleForm.startTime,
      end_time: scheduleForm.endTime,
    };
    if (generate) {
      const count = dayCount ?? 0;
      const confirmed = await confirm({
        title: `Создать ${count} ${plural(count, "тренировку", "тренировки", "тренировок")}?`,
        body: (
          <>
            <p>
              {`С ${formatDay(scheduleForm.from)} по ${formatDay(scheduleForm.to)}: ${weekdayList(scheduleWeekdays)}, ${formatTimeRange(
                scheduleForm.startTime,
                scheduleForm.endTime
              )}, ${scheduleTitle}.`}
            </p>
            <p>Детям группы придёт уведомление.</p>
            <p>
              Тренировки, которые уже есть в эти дни, останутся: сервер повторы не проверяет, и появятся дубли.
            </p>
          </>
        ),
        confirmLabel: "Создать тренировки",
      });
      if (!confirmed) return;
    }
    const schedule = currentSchedule;
    await run(async () => {
      if (schedule) await trainingsApi.updateSchedule(schedule.id, fields);
      else await trainingsApi.createSchedule(fields);
      if (generate) {
        await trainingsApi.generateTrainings({
          group_id: groupID,
          title: scheduleTitle,
          weekdays: scheduleWeekdays,
          start_date: scheduleForm.from,
          end_date: scheduleForm.to,
          type: "training",
          start_time: scheduleForm.startTime,
          end_time: scheduleForm.endTime,
          coach_id: scheduleForm.coachID ? Number(scheduleForm.coachID) : null,
        });
      }
      prefilledFor.current = null;
    }, "Не удалось сохранить недельное расписание");
  };

  const monthPrefix = dayString(month.year, month.month, 1).slice(0, 7);
  const scheduledInMonth = groupScheduled.filter((s) => s.date.startsWith(monthPrefix));
  const now = today();

  const convertScheduled = (s: ScheduledTraining) =>
    run(() => trainingsApi.convertScheduledTraining(s.id), "Не удалось сделать тренировку из запланированной");

  const removeScheduled = async (s: ScheduledTraining) => {
    const confirmed = await confirm({
      title: `Удалить запланированную тренировку ${formatDay(s.date)}?`,
      body: <p>Она пропадёт из очереди. Настоящие тренировки и посещаемость не изменятся.</p>,
      confirmLabel: "Удалить",
      danger: true,
    });
    if (!confirmed) return;
    await run(() => trainingsApi.deleteScheduledTraining(s.id), "Не удалось удалить запланированную тренировку");
  };

  if (loading) return <PageLoader />;

  const coachName = new Map(coaches.map((c) => [c.id, c.name]));
  const unknownPlace =
    edited?.place_id && !places.some((p) => p.id === edited.place_id)
      ? { id: edited.place_id, name: edited.place_name || edited.title }
      : null;

  return (
    <div className="page page-wide trainings-page">
      <h2>Расписание</h2>
      <p className="page-description">
        Тренировки и игры группы по месяцам, недельное расписание и посещаемость. Когда тренировку создают
        или удаляют, детям группы приходит уведомление.
      </p>

      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}

      {groups.length === 0 ? (
        !error && <p className="mav-status-muted">Групп пока нет — сначала создайте группу.</p>
      ) : (
        <>
          <div className="trainings-toolbar">
            <label className="cp-field trainings-group">
              <span>Группа</span>
              <select className="mav-input" value={groupID} onChange={(e) => chooseGroup(e.target.value)}>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </label>
            <div className="month-switch">
              <button
                type="button"
                className="mav-btn-secondary month-switch-btn"
                aria-label="Предыдущий месяц"
                onClick={() => shiftMonth(-1)}
              >
                ‹
              </button>
              <span className="month-switch-title" aria-live="polite">
                {`${MONTHS[month.month - 1]} ${month.year}`}
              </span>
              <button
                type="button"
                className="mav-btn-secondary month-switch-btn"
                aria-label="Следующий месяц"
                onClick={() => shiftMonth(1)}
              >
                ›
              </button>
            </div>
          </div>

          <section className="trainings-section" aria-labelledby="trainings-title">
            <div className="trainings-section-header">
              <h3 id="trainings-title">Тренировки</h3>
              <button type="button" className="mav-btn-primary" disabled={busy} onClick={startNew}>
                Новая тренировка
              </button>
            </div>

            {editing !== null && form && (
              <form
                ref={formRef}
                className="mav-card cp-form trainings-form"
                aria-label={edited ? "Изменение тренировки" : "Новая тренировка"}
                onSubmit={submitTraining}
              >
                <h3 className="trainings-form-title">
                  {edited ? `Изменение: ${formatDay(edited.date)}` : "Новая тренировка"}
                </h3>
                <label className="cp-field">
                  <span>Дата</span>
                  <input
                    className="mav-input"
                    type="date"
                    value={form.date}
                    disabled={busy}
                    onChange={(e) => setField({ date: e.target.value })}
                  />
                </label>
                <label className="cp-field">
                  <span>Тип</span>
                  <select
                    className="mav-input"
                    value={form.type}
                    disabled={busy}
                    onChange={(e) => setField({ type: e.target.value })}
                  >
                    <option value="training">Тренировка</option>
                    <option value="game">Игра</option>
                  </select>
                </label>
                <label className="cp-field">
                  <span>Начало</span>
                  <input
                    className="mav-input"
                    type="time"
                    value={form.startTime}
                    disabled={busy}
                    onChange={(e) => setField({ startTime: e.target.value })}
                  />
                </label>
                <div className="cp-field">
                  <label className="cp-field">
                    <span>Конец</span>
                    <input
                      className={trainingTimesInvalid ? "mav-input has-error" : "mav-input"}
                      type="time"
                      value={form.endTime}
                      disabled={busy}
                      onChange={(e) => setField({ endTime: e.target.value })}
                    />
                  </label>
                  {trainingTimesInvalid && <span className="mav-hint is-error">Конец должен быть позже начала</span>}
                </div>
                <label className="cp-field">
                  <span>Место</span>
                  <select
                    className="mav-input"
                    value={form.placeID}
                    disabled={busy}
                    onChange={(e) => setField({ placeID: e.target.value })}
                  >
                    {places.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                    {unknownPlace && <option value={unknownPlace.id}>{unknownPlace.name}</option>}
                    <option value={OTHER_PLACE}>Другое место</option>
                  </select>
                </label>
                {form.placeID === OTHER_PLACE ? (
                  <label className="cp-field">
                    <span>Адрес или название</span>
                    <input
                      className="mav-input"
                      value={form.title}
                      maxLength={200}
                      disabled={busy}
                      onChange={(e) => setField({ title: e.target.value })}
                    />
                  </label>
                ) : (
                  <span aria-hidden="true" />
                )}
                <label className="cp-field">
                  <span>Тренер</span>
                  <select
                    className="mav-input"
                    value={form.coachID}
                    disabled={busy}
                    onChange={(e) => setField({ coachID: e.target.value })}
                  >
                    <option value="">Без тренера</option>
                    {coaches.map((c) => (
                      <option key={c.id} value={String(c.id)}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </label>
                {canApplyOnward && (
                  <div className="trainings-onward">
                    <label className="trainings-check">
                      <input
                        type="checkbox"
                        checked={form.applyOnward}
                        disabled={busy}
                        onChange={(e) => setField({ applyOnward: e.target.checked })}
                      />
                      <span>
                        {`Применить новое время ко всем следующим тренировкам группы ${
                          isoWeekday(form.date) === 2 ? "во" : "в"
                        } ${weekdayOf(form.date).accusative}`}
                      </span>
                    </label>
                    <span className="mav-hint">С этой даты и дальше; игры не меняются</span>
                  </div>
                )}
                <div className="cp-actions mav-actions">
                  <button type="submit" className="mav-btn-primary" disabled={!canSubmitTraining}>
                    {edited ? "Сохранить" : "Создать тренировку"}
                  </button>
                  <button type="button" className="mav-btn-secondary" disabled={busy} onClick={() => setEditing(null)}>
                    Отмена
                  </button>
                </div>
              </form>
            )}

            <div className="table-wrap">
              <table className="settings-table trainings-table" aria-label="Тренировки">
                <thead>
                  <tr>
                    <th>Дата</th>
                    <th>Время</th>
                    <th>Место</th>
                    <th>Тренер</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {trainings.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="empty-cell">
                        В этом месяце тренировок нет
                      </td>
                    </tr>
                  ) : (
                    trainings.map((t) => {
                      const coach = t.coach_name || (t.coach_id != null ? coachName.get(t.coach_id) : undefined);
                      return (
                        <tr key={t.id} className={attendanceFor?.id === t.id ? "is-selected" : undefined}>
                          <td className="cell-training-date">
                            <span className="training-date">{formatDay(t.date)}</span>
                            <span className="training-weekday">{weekdayOf(t.date).name}</span>
                          </td>
                          <td className="cell-training-time">{formatTimeRange(t.start_time, t.end_time) || "—"}</td>
                          <td className="cell-training-place">
                            <span>{t.place_name || t.title}</span>
                            {t.type === "game" && <span className="mav-badge mav-badge-dirty">Игра</span>}
                          </td>
                          <td className="cell-training-coach">
                            {coach || <span className="mav-status-muted">Без тренера</span>}
                          </td>
                          <td className="cell-action">
                            <div className="row-actions">
                              <button
                                type="button"
                                className="mav-btn-secondary"
                                disabled={busy}
                                onClick={() => setAttendanceFor(t)}
                              >
                                Посещаемость
                              </button>
                              <button
                                type="button"
                                className="mav-btn-secondary"
                                disabled={busy}
                                onClick={() => startEditing(t)}
                              >
                                Изменить
                              </button>
                              <button
                                type="button"
                                className="btn-quiet-danger"
                                disabled={busy}
                                onClick={() => removeTraining(t)}
                              >
                                Удалить
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {attendanceFor && (
              <TrainingAttendance
                key={attendanceFor.id}
                training={attendanceFor}
                onClose={() => setAttendanceFor(null)}
              />
            )}
          </section>

          <section className="trainings-section" aria-labelledby="schedule-title">
            <h3 id="schedule-title">Недельное расписание</h3>
            <p className="page-description trainings-section-description">
              По недельному расписанию создаются настоящие тренировки на выбранный период. Место и тренер у
              них не привязываются к списку: место попадает в название, тренера можно выбрать ниже.
            </p>
            <p className="schedule-current">
              {currentSchedule ? (
                <>
                  <span className="schedule-current-label">Сейчас: </span>
                  <span>
                    {`${weekdayList(currentSchedule.weekdays)}, ${formatTimeRange(
                      currentSchedule.start_time,
                      currentSchedule.end_time
                    )}, ${currentSchedule.title}`}
                  </span>
                </>
              ) : (
                <span className="mav-status-muted">Расписания пока нет</span>
              )}
            </p>
            {groupSchedules.length > 1 && (
              <p className="mav-hint schedule-many">
                {`У группы ${groupSchedules.length} недельных расписания — сохранение заменит последнее из них.`}
              </p>
            )}

            <form
              className="mav-card cp-form trainings-form"
              aria-label="Недельное расписание"
              onSubmit={(e) => {
                e.preventDefault();
                saveSchedule(true);
              }}
            >
              <fieldset className="schedule-weekdays" disabled={busy}>
                <legend>Дни недели</legend>
                <div className="weekday-options">
                  {WEEKDAYS.map((d) => (
                    <label key={d.iso} className="weekday-option">
                      <input
                        type="checkbox"
                        checked={scheduleForm.weekdays.includes(d.iso)}
                        onChange={() => toggleWeekday(d.iso)}
                      />
                      <span>{d.short}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
              <label className="cp-field">
                <span>Начало</span>
                <input
                  className="mav-input"
                  type="time"
                  value={scheduleForm.startTime}
                  disabled={busy}
                  onChange={(e) => setSchedule({ startTime: e.target.value })}
                />
              </label>
              <div className="cp-field">
                <label className="cp-field">
                  <span>Конец</span>
                  <input
                    className={
                      scheduleForm.startTime && scheduleForm.endTime && !scheduleTimesValid
                        ? "mav-input has-error"
                        : "mav-input"
                    }
                    type="time"
                    value={scheduleForm.endTime}
                    disabled={busy}
                    onChange={(e) => setSchedule({ endTime: e.target.value })}
                  />
                </label>
                {scheduleForm.startTime && scheduleForm.endTime && !scheduleTimesValid && (
                  <span className="mav-hint is-error">Конец должен быть позже начала</span>
                )}
              </div>
              <label className="cp-field">
                <span>Место</span>
                <select
                  className="mav-input"
                  value={scheduleForm.placeID}
                  disabled={busy}
                  onChange={(e) => setSchedule({ placeID: e.target.value })}
                >
                  <option value="">Выберите место</option>
                  {places.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.address ? `${p.name}, ${p.address}` : p.name}
                    </option>
                  ))}
                  <option value={OTHER_PLACE}>Другое место</option>
                </select>
              </label>
              {scheduleForm.placeID === OTHER_PLACE ? (
                <label className="cp-field">
                  <span>Адрес или название</span>
                  <input
                    className="mav-input"
                    value={scheduleForm.title}
                    maxLength={200}
                    disabled={busy}
                    onChange={(e) => setSchedule({ title: e.target.value })}
                  />
                </label>
              ) : (
                <span aria-hidden="true" />
              )}
              <label className="cp-field">
                <span>С</span>
                <input
                  className="mav-input"
                  type="date"
                  value={scheduleForm.from}
                  disabled={busy}
                  onChange={(e) => setSchedule({ from: e.target.value })}
                />
              </label>
              <label className="cp-field">
                <span>По</span>
                <input
                  className="mav-input"
                  type="date"
                  value={scheduleForm.to}
                  disabled={busy}
                  onChange={(e) => setSchedule({ to: e.target.value })}
                />
              </label>
              <div className="cp-field">
                <label className="cp-field">
                  <span>Тренер</span>
                  <select
                    className="mav-input"
                    value={scheduleForm.coachID}
                    disabled={busy}
                    onChange={(e) => setSchedule({ coachID: e.target.value })}
                  >
                    <option value="">Без тренера</option>
                    {coaches.map((c) => (
                      <option key={c.id} value={String(c.id)}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </label>
                <span className="mav-hint">Только для создаваемых тренировок, в расписании не хранится</span>
              </div>
              {periodHint && (
                <p className={canGenerate || (dayCount ?? 0) > 0 ? "mav-hint schedule-period" : "mav-hint is-error schedule-period"}>
                  {periodHint}
                </p>
              )}
              <div className="cp-actions mav-actions">
                <button type="submit" className="mav-btn-primary" disabled={!canGenerate}>
                  Сохранить и создать тренировки
                </button>
                <button
                  type="button"
                  className="mav-btn-secondary"
                  disabled={!canSaveSchedule}
                  onClick={() => saveSchedule(false)}
                >
                  Сохранить расписание
                </button>
              </div>
            </form>
          </section>

          {groupScheduled.length > 0 && (
            <section className="trainings-section" aria-labelledby="scheduled-title">
              <h3 id="scheduled-title">Запланированные тренировки</h3>
              <p className="page-description trainings-section-description">
                Очередь из недельного расписания. Тренировку из запланированной сервер создаёт без тренера и
                места и без уведомления детям — тренера и место можно добавить потом через «Изменить».
              </p>
              {scheduledInMonth.length === 0 ? (
                <p className="mav-status-muted">В этом месяце запланированных тренировок нет</p>
              ) : (
                <div className="table-wrap">
                  <table className="settings-table trainings-table scheduled-table" aria-label="Запланированные тренировки">
                    <thead>
                      <tr>
                        <th>Дата</th>
                        <th>Время</th>
                        <th>Место</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {scheduledInMonth.map((s) => {
                        const past = s.date < now;
                        return (
                          <tr key={s.id}>
                            <td className="cell-training-date">
                              <span className="training-date">{formatDay(s.date)}</span>
                              <span className="training-weekday">{weekdayOf(s.date).name}</span>
                            </td>
                            <td className="cell-training-time">{formatTimeRange(s.start_time, s.end_time) || "—"}</td>
                            <td className="cell-training-place">
                              <span>{s.title}</span>
                              {past && <span className="mav-badge mav-badge-muted">Прошла</span>}
                            </td>
                            <td className="cell-action">
                              <div className="row-actions">
                                {!past && (
                                  <button
                                    type="button"
                                    className="mav-btn-secondary"
                                    disabled={busy}
                                    onClick={() => convertScheduled(s)}
                                  >
                                    Сделать тренировкой
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="btn-quiet-danger"
                                  disabled={busy}
                                  onClick={() => removeScheduled(s)}
                                >
                                  Удалить
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </>
      )}

      {dialog}
    </div>
  );
}
